import {
  ProgramType,
  DeviceStatus,
  type DeviceType,
} from "../../state/api.types";
import { programTypeToDeviceTypes } from "../../utilities/devices";

/**
 * Devices are filtered by the program they serve rather than by their own type:
 * an EV charging program is satisfied by either a charger or a vehicle, so
 * separating the two would offer a distinction the program does not make.
 */
export const ORDERED_DEVICE_PROGRAMS: ProgramType[] = [
  ProgramType.Battery,
  ProgramType.Evcharging,
];

/**
 * A dashboard concept, not an API one: the endpoint takes raw device statuses
 * plus a separate flag for accounts with nothing enrolled, and these three
 * options map onto both axes in `toDeviceStatusParams`.
 */
export const ORDERED_DEVICE_STATUSES = [
  "connected",
  "disconnected",
  "not_yet_connected",
] as const;

export type DeviceStatusFilter = (typeof ORDERED_DEVICE_STATUSES)[number];

const DEVICE_PROGRAM_LABELS: Record<ProgramType, string> = {
  [ProgramType.Battery]: "Battery",
  [ProgramType.Evcharging]: "EVs and chargers",
};

type DeviceStatusDisplay = {
  label: string;
  dotColor: string;
};

const DEVICE_STATUS_DISPLAY_MAP: Record<
  DeviceStatusFilter,
  DeviceStatusDisplay
> = {
  connected: { label: "Connected", dotColor: "text-fg-success-secondary" },
  disconnected: {
    label: "Needs attention",
    dotColor: "text-fg-warning-secondary",
  },
  not_yet_connected: {
    label: "Not yet connected",
    dotColor: "text-fg-quaternary",
  },
};

export type DeviceStatusParams = {
  device_status?: DeviceStatus[];
  missing_program_device?: boolean;
};

/** Translate the selected options into the endpoint's two axes. */
export function toDeviceStatusParams(
  selected: DeviceStatusFilter[],
): DeviceStatusParams {
  const statuses: DeviceStatus[] = [];
  if (selected.includes("connected")) {
    statuses.push(DeviceStatus.Active);
  }
  if (selected.includes("disconnected")) {
    statuses.push(DeviceStatus.NeedsAttention);
  }

  return {
    device_status: statuses.length > 0 ? statuses : undefined,
    missing_program_device: selected.includes("not_yet_connected") || undefined,
  };
}

export const DEVICE_FILTER_DESCRIPTION =
  "Devices can be attached to an account when its plan includes a battery or EV charging program.";

export function getDeviceProgramLabel(programType: ProgramType): string {
  return DEVICE_PROGRAM_LABELS[programType];
}

export function getDeviceStatusDisplay(
  deviceStatus: DeviceStatusFilter,
): DeviceStatusDisplay {
  return DEVICE_STATUS_DISPLAY_MAP[deviceStatus];
}

/** Expands the programs the filter offers into the device types it matches. */
export function toDeviceTypes(programTypes: ProgramType[]): DeviceType[] {
  return programTypes.flatMap((programType) => [
    ...programTypeToDeviceTypes[programType],
  ]);
}

export function parseDevicePrograms(values: string[]): ProgramType[] {
  return values.filter((value): value is ProgramType =>
    ORDERED_DEVICE_PROGRAMS.includes(value as ProgramType),
  );
}

export function parseDeviceStatuses(values: string[]): DeviceStatusFilter[] {
  return values.filter((value): value is DeviceStatusFilter =>
    ORDERED_DEVICE_STATUSES.includes(value as DeviceStatusFilter),
  );
}
